import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarCheck, CheckCircle2, Phone, ShieldCheck, Siren, Star } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import ProjectShowcase from "@/components/ProjectShowcase";
import { FadeUp, MaskLine, Eyebrow, EASE } from "@/components/Reveal";
import { goToSection } from "@/components/Nav";
import { SERVICES } from "@/data/services";
import { SITE, COMMITMENTS, CLIENT_TYPES } from "@/data/site";
import { AREAS } from "@/data/areas";
import api from "@/lib/api";

const FACTS = [
  { icon: Star, text: "5.0 from 27 Google reviews" },
  { icon: CalendarCheck, text: "Free on-site quotes" },
  { icon: Clock, text: SITE.hoursShort },
  { icon: Siren, text: "24/7 emergency calls" },
];

const HOME_FAQS = [
  {
    q: "How quickly can you help with broken glass?",
    a: "We provide same-day service and same-day replacement across Sydney wherever possible - and our emergency line takes calls 24/7. When we say we're coming, we'll be there.",
  },
  {
    q: "Can I get a free quote before work begins?",
    a: "Yes. Tell us what needs fixing and we'll provide a clear, obligation-free quote. The final price depends on the glass type, measurements, access and urgency of the job.",
  },
  {
    q: "Do you handle residential and commercial glass?",
    a: "Yes. We work across homes, offices and commercial properties - windows, shower screens, mirrors, splashbacks, pool fencing, pet doors, shopfronts, balustrades and replacement glass.",
  },
  {
    q: "Is the work guaranteed?",
    a: "Every job is backed by our workmanship guarantee plus full manufacturer and installation warranties, and all products and installations meet Australian Standard AS 1288.",
  },
];

function Faq({ faqs, testidBase = "home-faq" }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white" data-testid={testidBase}>
      {faqs.map((f, i) => (
        <div key={i}>
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
            data-testid={`${testidBase}-${i}`}
          >
            <span className="font-display text-sm font-bold text-slate-900 sm:text-base">{f.q}</span>
            <Chevron className={`h-4 w-4 shrink-0 text-brand-red transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <motion.div
            initial={false}
            animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{f.a}</p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

function Chevron({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function Clock({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

function HeroVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.5, duration: 0.8, ease: EASE }}
      className="relative mx-auto w-full max-w-md lg:max-w-none"
      data-testid="hero-visual"
    >
      <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_70px_rgba(16,24,40,0.25)]">
        <img
          src="/uploads/stainless/tp4.jpg"
          alt="Curved low iron glass stair balustrade with LED stainless steel handrail at a Tennyson Point waterfront home - installed by Fusion Glass"
          className="h-[420px] w-full object-cover lg:h-[540px]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6, ease: EASE }}
        className="absolute -bottom-6 -left-3 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-[0_16px_40px_rgba(16,24,40,0.15)] sm:-left-8"
        data-testid="hero-rating-chip"
      >
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
          ))}
        </div>
        <div>
          <p className="font-display text-sm font-extrabold text-slate-900">5.0 from 27 reviews</p>
          <p className="text-[11px] text-slate-500">on Google</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.6, ease: EASE }}
        className="absolute -top-5 right-4 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-[0_16px_40px_rgba(16,24,40,0.15)]"
        data-testid="hero-emergency-chip"
      >
        <span className="pulse-dot h-2.5 w-2.5 rounded-full bg-brand-red" />
        <div>
          <p className="font-display text-sm font-extrabold text-slate-900">24/7 emergency glazing</p>
          <a href={`tel:${SITE.phoneEmergency}`} className="text-[11px] font-bold text-brand-red" data-testid="hero-emergency-btn">
            {SITE.phoneEmergencyDisplay}
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Home() {
  const location = useLocation();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      const t = setTimeout(() => goToSection(id), 120);
      window.history.replaceState({}, "");
      return () => clearTimeout(t);
    }
  }, [location.state]);

  return (
    <main data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-paper to-white pt-14 pb-20 lg:pt-20 lg:pb-24">
        <div className="container-x relative grid items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <Eyebrow className="mb-6 inline-flex items-center gap-3">
                <span className="h-px w-10 bg-brand-red" /> Fusion Glass - Sydney
              </Eyebrow>
            </motion.div>

            <h1 className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              <MaskLine delay={0.15}>Broken glass?</MaskLine>
              <MaskLine delay={0.3}>
                <span className="text-brand-red">We fix it. Fast.</span>
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Fast, reliable glass repairs and custom glass for homes and businesses across Sydney -
              pool fencing, balustrades, shower screens, mirrors and more. Made to measure,
              compliant with Australian Standard AS 1288, backed by our workmanship guarantee.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button onClick={() => goToSection("quote")} className="btn-red" data-testid="hero-quote-btn">
                Get a free quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href={`tel:${SITE.phoneMain}`} className="btn-outline" data-testid="hero-call-btn">
                <Phone className="h-4 w-4 text-brand-red" />
                Call {SITE.phoneMainDisplay}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-10 flex items-center gap-3"
            >
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">5.0</span> from 27 Google reviews
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* ============ FACT STRIP ============ */}
      <section className="border-y border-slate-100 bg-white">
        <div className="container-x grid grid-cols-2 gap-6 py-6 md:grid-cols-4">
          {FACTS.map((f) => (
            <div key={f.text} className="flex items-center gap-3">
              <f.icon className="h-5 w-5 shrink-0 text-brand-red" />
              <span className="text-sm font-semibold text-slate-800">{f.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="section-pad" id="services" data-testid="services-section">
        <div className="container-x">
          <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Eyebrow className="mb-4 block">Glass services, sorted</Eyebrow>
              <FadeUp>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                  What can we help with?
                </h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.1}>
              <p className="max-w-md text-sm leading-relaxed text-slate-600">
                Repairs, replacements and made-to-measure glass for homes and businesses,
                supplied and fitted by local Sydney professionals.
              </p>
            </FadeUp>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="services-grid">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.slug} delay={Math.min(i * 0.04, 0.4)} className="h-full">
                <Link
                  to={`/services/${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-brand-red/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.12)]"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-900 opacity-0 backdrop-blur-md transition-all duration-300 group-hover:opacity-100 group-hover:text-brand-red">
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="flex flex-1 items-center p-4">
                    <h3 className="font-display text-sm font-extrabold text-slate-900 sm:text-base">{s.tileName || s.title.split(" Sydney")[0]}</h3>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROJECT SHOWCASE ============ */}
      <ProjectShowcase />

      {/* ============ EMERGENCY BAND ============ */}
      <section className="bg-brand-red" data-testid="emergency-band">
        <div className="container-x flex flex-col items-start gap-8 py-14 md:py-16 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <Eyebrow className="mb-4 block !text-white/80">Emergency glazing</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Cracked glass at your home, office or shop?
              </h2>
              <p className="mt-3 text-white/85">
                Don't panic - we've got you sorted. Same-day service and same-day replacement,
                and we actually answer our calls.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="flex flex-wrap gap-4">
            <a
              href={`tel:${SITE.phoneEmergency}`}
              className="flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-brand-red transition-all hover:shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
              data-testid="emergency-call-btn"
            >
              <Siren className="h-4 w-4" /> {SITE.phoneEmergencyDisplay}
            </a>
            <Link
              to="/contact"
              className="rounded-full border-2 border-white/60 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/10"
              data-testid="emergency-quote-btn"
            >
              Free quote online
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ============ WHY FUSION ============ */}
      <section className="section-pad" id="why" data-testid="why-section">
        <div className="container-x">
          <div className="mb-12 text-center">
            <Eyebrow className="mb-4 block">Why Fusion Glass?</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Family-run. Local expertise. <span className="text-brand-red">Guaranteed work.</span>
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                Fusion Glass is a family business run by three main installers and one admin - the same small team
                that measures your job is the team that installs it and hands it over. No subcontractors, just
                responsive local service and quality workmanship, from urgent repairs to custom glass.
              </p>
            </FadeUp>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COMMITMENTS.slice(0, 4).map((c, i) => (
              <FadeUp key={c.title} delay={Math.min(i * 0.07, 0.35)}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 transition-all duration-300 hover:border-brand-red/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.1)]">
                  <CheckCircle2 className="mb-4 h-6 w-6 text-brand-red" />
                  <h3 className="font-display text-base font-extrabold text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {COMMITMENTS.slice(4).map((c, i) => (
              <FadeUp key={c.title} delay={0.1 + i * 0.07}>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-brand-paper p-6">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                  <div>
                    <h3 className="font-display text-base font-extrabold text-slate-900">{c.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{c.text}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      {reviews.length > 0 && (
        <section className="border-y border-slate-100 bg-brand-paper py-16 md:py-24" id="reviews" data-testid="reviews-section">
          <div className="container-x">
            <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <Eyebrow className="mb-4 block">Customers say it best</Eyebrow>
                <FadeUp>
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                    Good glass work <span className="text-brand-red">gets people talking.</span>
                  </h2>
                </FadeUp>
              </div>
              <FadeUp delay={0.1}>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Fusion+Glass+Sydney+Olympic+Park"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-5 py-2.5 text-sm font-bold text-slate-900 transition-colors hover:border-brand-red hover:text-brand-red"
                    data-testid="google-reviews-badge"
                  >
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    5.0 from 27 reviews on Google
                  </a>
                  <a
                    href={SITE.reviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-red !px-5 !py-2.5 !text-sm"
                    data-testid="leave-review-btn"
                  >
                    Leave us a Google review
                  </a>
                </div>
              </FadeUp>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {reviews.slice(0, 6).map((r, i) => (
                <FadeUp key={r.id} delay={Math.min(i * 0.07, 0.3)}>
                  <div className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-7 shadow-[0_4px_20px_rgba(16,24,40,0.05)]">
                    <div className="mb-4 flex gap-1">
                      {Array.from({ length: r.rating }).map((_, s) => (
                        <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="flex-1 text-sm leading-relaxed text-slate-700">"{r.text}"</p>
                    <p className="mt-5 font-display text-sm font-extrabold text-slate-900">
                      {r.customer_name}
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">Google review</span>
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ REGIONS ============ */}
      <section className="section-pad" id="regions" data-testid="regions-section">
        <div className="container-x">
          <div className="mb-12">
            <Eyebrow className="mb-4 block">Where we work</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Servicing all of <span className="text-brand-red">Greater Sydney</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-4 max-w-2xl text-sm text-slate-600">
                Fast onsite measure and quote across every region - call us and we'll be there.
              </p>
            </FadeUp>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AREAS.map((r, i) => (
              <FadeUp key={r.name} delay={Math.min(i * 0.04, 0.35)}>
                <Link
                  to={`/areas/${r.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 transition-colors hover:border-brand-red/40"
                  data-testid={`region-link-${r.slug}`}
                >
                  <h3 className="flex items-center justify-between font-display text-sm font-extrabold uppercase tracking-wider text-brand-red">
                    {r.name}
                    <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-brand-red" />
                  </h3>
                  <p className="mt-3 text-xs leading-loose text-slate-500">{r.suburbs.join(" · ")}</p>
                  <span className="mt-4 text-xs font-bold text-slate-900 underline-offset-4 group-hover:underline">Glaziers in the {r.name}</span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="border-y border-slate-100 bg-brand-paper py-16 md:py-24" data-testid="home-faq-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow className="mb-4 block">Good to know</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                Have questions about <span className="text-brand-red">Fusion Glass?</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-600">
                Everything you need to know before booking. For anything else, call us on{" "}
                <a href={`tel:${SITE.phoneMain}`} className="font-bold text-brand-red">
                  {SITE.phoneMainDisplay}
                </a>{" "}
                - we answer our phone.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <Faq faqs={HOME_FAQS} />
          </div>
        </div>
      </section>

      {/* ============ QUOTE BAND ============ */}
      <section className="relative overflow-hidden py-16 md:py-24" id="quote" data-testid="quote-section">
        <div className="container-x relative grid items-center gap-14 lg:grid-cols-2">
          <div>
            <Eyebrow className="mb-4 block">Free quote, no obligation</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Your local Fusion is ready.
                <br />
                <span className="text-brand-red">Let's get your glass sorted.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-600">
                Send through a few details and we'll come back with a clear price - usually the same day.
                Prefer to talk? We answer our phone.
              </p>
              <p className="mt-4 max-w-md text-xs leading-relaxed text-slate-500">
                Trusted by {CLIENT_TYPES}.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-8 space-y-3">
                <a href={`tel:${SITE.phoneMain}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-brand-red/50" data-testid="quote-band-call-btn">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-red/10">
                    <Phone className="h-5 w-5 text-brand-red" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-extrabold text-slate-900">{SITE.phoneMainDisplay}</p>
                    <p className="text-xs text-slate-500">{SITE.hours}</p>
                  </div>
                </a>
                <a href={`tel:${SITE.phoneEmergency}`} className="flex items-center gap-4 rounded-2xl border-2 border-brand-red/20 bg-brand-red/[0.04] p-5 transition-colors hover:border-brand-red/60" data-testid="quote-band-emergency-btn">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-red text-white">
                    <Siren className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-extrabold text-slate-900">{SITE.phoneEmergencyDisplay}</p>
                    <p className="text-xs text-slate-500">24/7 for emergency glass</p>
                  </div>
                </a>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(16,24,40,0.12)] sm:p-8">
              <QuoteForm />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
